import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { MessageSquare, Plus, User, Send, Edit2, Trash2 } from 'lucide-react';
import apiService from '../services/apiService';

const Container = styled.div`
  min-height: 100vh;
  background: #141414;
  color: #e5e5e5;
  padding: 80px 20px 20px;
  
  @media (max-width: 600px) {
    padding: 70px 12px 80px; /* Mobile padding */
  }
`;

const Header = styled.div`
  max-width: 800px;
  margin: 0 auto 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #fff;
  
  span {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
  }
`;

const WriteButton = styled.button`
  background: #10b981;
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 20px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.2s;
  
  &:hover {
    background: #059669;
    transform: scale(1.05);
  }
`;

const PostList = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PostCard = styled.div`
  background: #1f1f1f;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
  transition: border-color 0.2s;
  
  &:hover {
    border-color: #555;
  }
`;

const PostHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 0.9rem;
  color: #888;
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #aaa;
`;

const PostTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 8px;
  color: #fff;
`;

const PostContent = styled.p`
  font-size: 1rem;
  color: #d4d4d4;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const WriteModal = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #1f1f1f;
  border: 1px solid #333;
  width: 100%;
  max-width: 600px;
  border-radius: 16px;
  padding: 24px;
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  background: #141414;
  border: 1px solid #333;
  color: #fff;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 1.1rem;
  
  &:focus {
      outline: none;
      border-color: #10b981;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 200px;
  background: #141414;
  border: 1px solid #333;
  color: #fff;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  
  &:focus {
      outline: none;
      border-color: #10b981;
  }
`;

const ActionButton = styled.button`
    width: 100%;
    padding: 14px;
    border-radius: 8px;
    background: #10b981;
    color: white;
    font-weight: bold;
    font-size: 1rem;
    border: none;
    cursor: pointer;
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

function CommunityPage() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isWriting, setIsWriting] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newContent, setNewContent] = useState('');

    const user = JSON.parse(localStorage.getItem('user'));

    /* Edit & Delete State */
    const [editingPostId, setEditingPostId] = useState(null);

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = async () => {
        setLoading(true);
        try {
            const res = await apiService.request('/board');
            setPosts(res.data);
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    const handleDelete = async (postId) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        try {
            // DELETE requests often need custom config for body in fetch/axios, 
            // apiService.request handles simple JSON body if method is passed.
            // Note: Standard fetch DELETE with body is valid but sometimes tricky. 
            // Our apiService.request uses fetch.
            await apiService.request(`/board/${postId}`, {
                method: 'DELETE',
                body: JSON.stringify({ userId: user?.id })
            });
            fetchPosts();
        } catch (err) {
            alert('삭제 실패: ' + err.message);
        }
    };

    const openEdit = (post) => {
        setEditingPostId(post.id);
        setNewTitle(post.title);
        setNewContent(post.content);
        setIsWriting(true);
    };

    const handleCloseModal = () => {
        setIsWriting(false);
        setEditingPostId(null);
        setNewTitle('');
        setNewContent('');
    };

    const handleSubmit = async () => {
        if (!newTitle.trim() || !newContent.trim()) return;

        try {
            if (editingPostId) {
                // Update
                await apiService.request(`/board/${editingPostId}`, {
                    method: 'PUT',
                    body: JSON.stringify({
                        userId: user?.id,
                        title: newTitle,
                        content: newContent
                    })
                });
            } else {
                // Create
                await apiService.request('/board', {
                    method: 'POST',
                    body: JSON.stringify({
                        userId: user?.id,
                        authorName: user?.email ? user.email.split('@')[0] : '익명',
                        title: newTitle,
                        content: newContent
                    })
                });
            }
            handleCloseModal();
            fetchPosts(); // Refresh
        } catch (err) {
            alert('작성/수정 실패');
        }
    };

    return (
        <Container>
            <Header>
                <Title>
                    <MessageSquare color="#10b981" size={32} />
                    <span>소통게시판</span>
                </Title>
                <WriteButton onClick={() => setIsWriting(true)}>
                    <Plus size={18} />
                    글쓰기
                </WriteButton>
            </Header>

            <PostList>
                {loading && <div style={{ textAlign: 'center' }}>로딩 중...</div>}
                {!loading && posts.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#555', padding: '40px' }}>
                        첫 번째 글의 주인공이 되어보세요!
                    </div>
                )}
                {posts.map(post => (
                    <PostCard key={post.id}>
                        <PostHeader>
                            <Author>
                                <User size={14} />
                                {post.authorName}
                            </Author>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span>{new Date(post.createdAt?._seconds * 1000).toLocaleDateString()}</span>
                                {user && user.id === post.userId && (
                                    <>
                                        <button
                                            onClick={() => openEdit(post)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}
                                            title="수정"
                                        >
                                            <Edit2 size={14} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(post.id)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}
                                            title="삭제"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </>
                                )}
                            </div>
                        </PostHeader>
                        <PostTitle>{post.title}</PostTitle>
                        <PostContent>{post.content}</PostContent>
                    </PostCard>
                ))}
            </PostList>

            {isWriting && (
                <WriteModal onClick={handleCloseModal}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <h2 style={{ marginTop: 0, marginBottom: 20 }}>
                            {editingPostId ? '글 수정' : '새 글 작성'}
                        </h2>
                        <Input
                            placeholder="제목을 입력하세요"
                            value={newTitle}
                            onChange={e => setNewTitle(e.target.value)}
                        />
                        <TextArea
                            placeholder="자유롭게 의견을 남겨주세요."
                            value={newContent}
                            onChange={e => setNewContent(e.target.value)}
                        />
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <ActionButton
                                style={{ background: '#333' }}
                                onClick={handleCloseModal}
                            >
                                취소
                            </ActionButton>
                            <ActionButton onClick={handleSubmit}>
                                {editingPostId ? '수정 완료' : '등록하기'}
                            </ActionButton>
                        </div>
                    </ModalContent>
                </WriteModal>
            )}
        </Container>
    );
}

export default CommunityPage;
